import React, { useMemo, useRef, useState } from 'react';
import { ColDef, ColumnState, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Select from 'src/components/common/gridWrapper/Grid_Select';
import { PDA1120_R01Dto } from 'src/containers/PDA/PDA1120_R01/PDA1120_R01Dto';
import {
  aggridHeaderClass,
  pdaCellStyle,
} from 'src/components/common/common';

interface PDA1120_R01TableProps {
  orderList: PDA1120_R01Dto[];
  onSelectOrder: (order: PDA1120_R01Dto) => void;
}

type PDA1120_R01DtoWithInserted = PDA1120_R01Dto & { inserted?: boolean };

/**
 * PDA1120_R01 공정지시 목록 그리드
 */
const PDA1120_R01Table: React.FC<PDA1120_R01TableProps> = ({
  orderList,
  onSelectOrder,
}) => {
  const gridRef = useRef<AgGridReact<PDA1120_R01DtoWithInserted>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);

  // 그리드 데이터
  const paddedGridData = useMemo(() => {
    const data: PDA1120_R01DtoWithInserted[] = [...orderList];
    return data;
  }, [orderList]);

  // 컬럼 정의
  const columnDefs = useMemo(
    () => [
      {
        headerName: '일자',
        field: 'workDt',
        valueGetter: (params) => {
          const dateStr = params.data?.workDt;
          if (!dateStr) return '-';
          // YYYY-MM-DD 형식을 MM-DD로 변환
          const parts = dateStr.split('-');
          if (parts.length >= 3) {
            return `${parts[1]}-${parts[2]}`;
          }
          return dateStr;
        },
        cellStyle: {
          textAlign: 'center',
          ...pdaCellStyle,
          justifyContent: 'center'
        },
        headerClass: aggridHeaderClass,
        flex: 0.7,
        minWidth: 90,
      },
      {
        headerName: '코드',
        field: 'productWorkNo',
        valueGetter: (params) => params.data?.productWorkNo || '-',
        cellStyle: {
          textAlign: 'left',
          ...pdaCellStyle,
          justifyContent: 'left'
        },
        headerClass: aggridHeaderClass,
        flex: 1.2,
        minWidth: 90,
      },
      {
        headerName: '품명',
        field: 'itemNm',
        valueGetter: (params) => params.data?.itemNm || '-',
        cellStyle: {
          textAlign: 'left',
          ...pdaCellStyle,
        },
        headerClass: aggridHeaderClass,
        flex: 1.5,
        minWidth: 90,
      },
      {
        headerName: '규격',
        field: 'itemSpec',
        valueGetter: (params) => params.data?.itemSpec || '-',
        cellStyle: {
          textAlign: 'left',
          ...pdaCellStyle,
        },
        headerClass: aggridHeaderClass,
        flex: 2,
        minWidth: 80,
      },
    ] as ColDef<PDA1120_R01DtoWithInserted>[],
    []
  );

  // 행 클릭 이벤트
  const handleRowClicked = (event: RowClickedEvent<PDA1120_R01DtoWithInserted>) => {
    if (event.data) {
      onSelectOrder(event.data);
    }
  };

  // 선택 초기화 함수
  const initSelected = () => {};

  return (
    <div className="flex-1 p-3 pt-2 flex w-full h-full flex-col overflow-hidden">
      <GridContextWrapper
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={paddedGridData}
        initSelected={initSelected}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1120_R01_OrderList_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<PDA1120_R01DtoWithInserted>
            ref={gridRef}
            rowData={paddedGridData}
            columnDefs={columnDefs}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1120_R01_OrderList_ColumnState"
            setColumnState={setColumnState}
            onRowClicked={handleRowClicked}
          />
        </div>
      </GridContextWrapper>
    </div>
  );
};

export default PDA1120_R01Table;
